/**
 * PositioningGuide — Guided (not instructional) setup for decompression position
 * Dark/gold scheme matching the Heart Flow experience
 */

import { motion } from 'framer-motion';

const PositioningGuide = ({ onReady }) => {
  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center px-8"
      style={{ background: 'radial-gradient(ellipse at 50% 45%, rgba(25,18,10,1) 0%, rgba(10,7,4,1) 65%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      data-testid="positioning-guide"
    >
      {/* Soft warm ambient glow */}
      <motion.div
        className="fixed inset-0 pointer-events-none"
        animate={{
          background: [
            'radial-gradient(ellipse 50% 40% at 50% 50%, rgba(200,165,115,0.03) 0%, transparent 70%)',
            'radial-gradient(ellipse 55% 45% at 50% 50%, rgba(210,175,125,0.05) 0%, transparent 70%)',
            'radial-gradient(ellipse 50% 40% at 50% 50%, rgba(200,165,115,0.03) 0%, transparent 70%)',
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="z-10 text-center max-w-sm">
        {/* Label */}
        <motion.p
          className="text-white/20 text-xs tracking-[0.2em] uppercase mb-10"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Positioning
        </motion.p>

        {/* Soft warm shape suggesting rest */}
        <motion.div
          className="w-28 h-28 mx-auto mb-10 rounded-2xl flex items-center justify-center"
          style={{
            background: 'rgba(200,165,115,0.06)',
            border: '1px solid rgba(200,165,115,0.08)',
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M8 36C8 36 14 24 24 24C34 24 40 36 40 36" stroke="rgba(210,175,130,0.25)" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="24" cy="18" r="4" stroke="rgba(210,175,130,0.2)" strokeWidth="1" />
            <line x1="12" y1="40" x2="36" y2="40" stroke="rgba(210,175,130,0.12)" strokeWidth="1" />
          </svg>
        </motion.div>

        {/* Primary guidance — poetic, not instructional */}
        <motion.p
          className="text-white/55 text-lg mb-3 leading-relaxed whitespace-pre-line"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          {'Gently lie back\u2026\nand allow your body to be supported'}
        </motion.p>

        {/* Secondary practical note */}
        <motion.p
          className="text-white/22 text-xs mb-14 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          You may place your legs on a chair or cushion
        </motion.p>

        {/* Ready button — warm gold */}
        <motion.button
          onClick={onReady}
          className="px-10 py-3.5 rounded-full text-sm text-white/50"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
            background: 'rgba(200,165,115,0.1)',
            border: '1px solid rgba(200,165,115,0.15)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          data-testid="positioning-ready-btn"
        >
          I'm ready
        </motion.button>
      </div>

      {/* Disclaimer */}
      <div className="absolute bottom-6 px-8 text-center">
        <p className="text-white/10 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          This experience supports relaxation alongside your medical care.
        </p>
      </div>
    </motion.div>
  );
};

export default PositioningGuide;
